// src/store/modules/salaryManagement.js
import api from '@/api';

export default {
  namespaced: true,
  state: () => ({
    receipts: [],
    statements: [],
    // NEW: holds ALL statements (teacher + user) for the global view
    allStatements: [], // <-- NEW
    statementsPagination: { currentPage: 1, totalPages: 1, totalItems: 0, perPage: 10 },
    currentReceipt: null,
    isLoading: false,
    pagination: { currentPage: 1, totalPages: 1, totalItems: 0, perPage: 10 },
    filters: { type: '', search: '' },
    calculations: [],
    calculationsPagination: { currentPage: 1, totalPages: 1, totalItems: 0, perPage: 20 },
    currentCalculation: null,
    currentCalculationStatements: [],
    dashboard: null,
  }),
  mutations: {
    SET_RECEIPTS(state, receipts) { state.receipts = Array.isArray(receipts) ? receipts : []; },
    SET_STATEMENTS(state, statements) { state.statements = Array.isArray(statements) ? statements : []; },
    // NEW
    SET_ALL_STATEMENTS(state, items) { state.allStatements = Array.isArray(items) ? items : []; }, // <-- NEW
    SET_STATEMENTS_PAGINATION(state, p) { state.statementsPagination = { ...state.statementsPagination, ...p }; },
    SET_CURRENT_RECEIPT(state, receipt) { state.currentReceipt = receipt; },
    SET_LOADING(state, v) { state.isLoading = v; },
    SET_PAGINATION(state, p) { state.pagination = { ...state.pagination, ...p }; },
    SET_FILTERS(state, f) { state.filters = { ...state.filters, ...f }; },
    ADD_RECEIPT(state, r) { if (r?.id) state.receipts.unshift(r); },
    UPDATE_RECEIPT(state, r) {
      if (!r?.id) return;
      const idx = state.receipts.findIndex(x => x?.id === r.id);
      if (idx !== -1) state.receipts.splice(idx, 1, r);
    },
    DELETE_RECEIPT(state, id) {
      state.receipts = state.receipts.filter(r => r && r.id !== id);
    },
    SET_CALCULATIONS(state, items) { state.calculations = Array.isArray(items) ? items : []; },
    SET_CALCULATIONS_PAGINATION(state, p) { state.calculationsPagination = { ...state.calculationsPagination, ...p }; },
    SET_CURRENT_CALCULATION(state, item) { state.currentCalculation = item; },
    SET_CURRENT_CALCULATION_STATEMENTS(state, items) { state.currentCalculationStatements = Array.isArray(items) ? items : []; },
    SET_DASHBOARD(state, data) { state.dashboard = data; },
  },
  actions: {
    async fetchReceipts({ commit, state }) {
      commit('SET_LOADING', true);
      try {
        const res = await api.get('/salary-management', {
          params: { page: state.pagination.currentPage, per_page: state.pagination.perPage, ...state.filters }
        });
        const receipts = res.data.data || res.data || [];
        commit('SET_RECEIPTS', receipts);
        const meta = res.data.meta || {};
        commit('SET_PAGINATION', {
          currentPage: meta.current_page || 1,
          totalPages: meta.last_page || 1,
          totalItems: meta.total || receipts.length,
          perPage: meta.per_page || state.pagination.perPage
        });
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchStatements({ commit }, { modelType, modelId, page = 1, perPage = 10 }) {
      const res = await api.get(`/salary-management/${modelType}/${modelId}/statements`, {
        params: { page, per_page: perPage }
      });
      const items = res.data.data || res.data || [];
      commit('SET_STATEMENTS', items);
      const meta = res.data.meta || {};
      commit('SET_STATEMENTS_PAGINATION', {
        currentPage: meta.current_page || page,
        totalPages: meta.last_page || 1,
        totalItems: meta.total || items.length,
        perPage: meta.per_page || perPage
      });
    },

    // NEW: get ALL statements (teachers + users) once, for client-side filtering
    async fetchAllStatements({ commit }) {
      const res = await api.get('/salary-management/statements/all'); // <-- NEW endpoint
      // Expecting a flat array of statements
      const items = res.data.data || res.data || [];

      // Optional: normalize fields the page expects
      const normalized = items.map(s => ({
        id: s.id,
        date: s.date,
        description: s.description ?? '',
        debit: Number(s.debit) || 0,
        credit: Number(s.credit) || 0,
        balance: Number(s.balance) || 0,
        model_name: s.model_name || s.model?.username || s.model?.name || '—',
      }));

      commit('SET_ALL_STATEMENTS', normalized);
      return normalized;
    },

    async paySalary({ commit }, { modelType, modelId, data }) {
      const res = await api.post(`/salary-management/${modelType}/${modelId}/pay-salary`, data);
      const receipt = res.data.data || res.data;
      if (receipt) commit('ADD_RECEIPT', receipt);
      return res.data;
    },

    async addReward({ commit }, { modelType, modelId, data }) {
      const res = await api.post(`/salary-management/${modelType}/${modelId}/add-reward`, data);
      const receipt = res.data.data || res.data;
      if (receipt) commit('ADD_RECEIPT', receipt);
      return res.data;
    },

    async updateReceipt({ commit }, { id, data }) {
      const res = await api.put(`/salary-management/receipts/${id}`, data);
      const receipt = res.data.data || res.data;
      if (receipt) commit('UPDATE_RECEIPT', receipt);
      return res.data;
    },

    async processMonthlySalaries(_, data) {
      const res = await api.post('/salary-management/process-monthly', data);
      return res.data;
    },

    async checkMonthProcessed(_, month) {
      const res = await api.get('/salary-management/check-processed', { params: { month } });
      return res.data;
    },

    async addDeduction({ commit }, { modelType, modelId, data }) {
      const res = await api.post(`/salary-management/${modelType}/${modelId}/add-deduction`, data);
      const receipt = res.data.data || res.data;
      if (receipt) commit('ADD_RECEIPT', receipt);
      return res.data;
    },

    async fetchCalculations({ commit, state }, filters = {}) {
      commit('SET_LOADING', true);
      try {
        const res = await api.get('/salary-management/calculations', {
          params: {
            page: state.calculationsPagination.currentPage,
            per_page: state.calculationsPagination.perPage,
            ...filters,
          }
        });
        const items = res.data.data || res.data || [];
        commit('SET_CALCULATIONS', items);
        const meta = res.data.meta || {};
        commit('SET_CALCULATIONS_PAGINATION', {
          currentPage: meta.current_page || 1,
          totalPages: meta.last_page || 1,
          totalItems: meta.total || items.length,
          perPage: meta.per_page || state.calculationsPagination.perPage,
        });
        return items;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchDashboard({ commit }, filters = {}) {
      const res = await api.get('/salary-management/dashboard', { params: filters });
      commit('SET_DASHBOARD', res.data);
      return res.data;
    },

    async fetchCalculationDetail({ commit }, id) {
      const res = await api.get(`/salary-management/calculations/${id}`);
      commit('SET_CURRENT_CALCULATION', res.data.data);
      commit('SET_CURRENT_CALCULATION_STATEMENTS', res.data.statements?.data || res.data.statements || []);
      return res.data;
    },

    async deleteReceipt({ commit }, id) {
      await api.delete(`/salary-management/receipts/${id}`);
      commit('DELETE_RECEIPT', id);
      return { success: true };
    },

    async deleteCalculation({ state, commit }, id) {
      const res = await api.delete(`/salary-management/calculations/${id}`);
      commit('SET_CALCULATIONS', state.calculations.filter(c => c.id !== id));
      return res.data;
    },

    updateFilters({ commit }, f) { commit('SET_FILTERS', f); },
    resetFilters({ commit }) { commit('SET_FILTERS', { type: '', search: '' }); }
  },
  getters: {
    receipts: s => (Array.isArray(s.receipts) ? s.receipts : []),
    statements: s => (Array.isArray(s.statements) ? s.statements : []),
    // NEW
    allStatements: s => (Array.isArray(s.allStatements) ? s.allStatements : []), // <-- NEW
    isLoading: s => s.isLoading,
    pagination: s => s.pagination,
    statementsPagination: s => s.statementsPagination,
    filters: s => s.filters,
    calculations: s => (Array.isArray(s.calculations) ? s.calculations : []),
    calculationsPagination: s => s.calculationsPagination,
    currentCalculation: s => s.currentCalculation,
    currentCalculationStatements: s => s.currentCalculationStatements,
    dashboard: s => s.dashboard,
  }
};
